import React, { useState } from 'react';
import { Plus, Trash2, TrendingDown, Receipt, Check } from 'lucide-react';
import { format } from 'date-fns';
import { Button } from '../../../components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../../components/ui/table';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select';

const CATEGORIES = ['maintenance', 'supplies', 'salary', 'utilities', 'food', 'transport', 'equipment', 'marketing', 'other'];
const METHODS = ['cash', 'upi', 'card', 'bank_transfer'];

const fmtDate = (dateStr) => {
  if (!dateStr) return '';
  const parts = dateStr.split('-');
  if (parts.length === 3) return `${parts[2]}/${parts[1]}/${parts[0]}`;
  return dateStr;
};

const emptyRow = () => ({ date: format(new Date(), 'yyyy-MM-dd'), category: 'maintenance', description: '', amount: '', payment_method: 'cash', notes: '' });

export const ExpensesTab = ({ expenses, expenseSummary, expenseFilter, setExpenseFilter, setShowExpenseDialog, deleteExpense, createExpense }) => {
  const [row, setRow] = useState(emptyRow());
  const [saving, setSaving] = useState(false);

  const addRow = async () => {
    if (!row.description.trim() || !row.amount || parseFloat(row.amount) <= 0) return;
    setSaving(true);
    try {
      await createExpense(row);
      // keep date/category/method for fast repeat entry; clear description + amount
      setRow(prev => ({ ...emptyRow(), date: prev.date, category: prev.category, payment_method: prev.payment_method }));
    } catch (e) { console.error(e); }
    finally { setSaving(false); }
  };

  const onKey = (e) => { if (e.key === 'Enter') { e.preventDefault(); addRow(); } };

  return (
    <div data-testid="expenses-content">
      <div className="flex flex-wrap gap-4 mb-6 items-end justify-between">
        <div className="flex flex-wrap gap-4 items-end">
          <div>
            <Label className="text-sm text-[#5E6E60]">Category</Label>
            <Select value={expenseFilter.category} onValueChange={(v) => setExpenseFilter(prev => ({ ...prev, category: v }))}>
              <SelectTrigger className="w-40 mt-1"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                {CATEGORIES.map(c => <SelectItem key={c} value={c} className="capitalize">{c}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-sm text-[#5E6E60]">From</Label>
            <Input type="date" value={expenseFilter.start} onChange={(e) => setExpenseFilter(prev => ({ ...prev, start: e.target.value }))} className="mt-1 w-40" />
          </div>
          <div>
            <Label className="text-sm text-[#5E6E60]">To</Label>
            <Input type="date" value={expenseFilter.end} onChange={(e) => setExpenseFilter(prev => ({ ...prev, end: e.target.value }))} className="mt-1 w-40" />
          </div>
        </div>
        <Button onClick={() => setShowExpenseDialog(true)} variant="outline" className="border-[#2F5233]/40 text-[#2F5233]" data-testid="add-expense-btn"><Plus size={16} className="mr-2" /> Add via Form</Button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
        <div className="bg-[#FFFFFF] p-5 rounded-lg border border-[#2F5233]/30">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center"><TrendingDown size={20} className="text-red-600" /></div>
            <div><p className="text-sm text-[#5E6E60]">Total Expenses</p><p className="text-xl font-semibold text-red-600">₹{(expenseSummary.total_expenses || 0).toLocaleString()}</p></div>
          </div>
        </div>
        <div className="bg-[#FFFFFF] p-5 rounded-lg border border-[#2F5233]/30">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center"><Receipt size={20} className="text-blue-600" /></div>
            <div><p className="text-sm text-[#5E6E60]">Total Records</p><p className="text-xl font-semibold text-[#1C2A20]">{expenseSummary.count || 0}</p></div>
          </div>
        </div>
        <div className="bg-[#FFFFFF] p-5 rounded-lg border border-[#2F5233]/30">
          <p className="text-sm text-[#5E6E60] mb-2">By Category</p>
          <div className="space-y-1">
            {Object.entries(expenseSummary.by_category || {}).sort((a, b) => b[1] - a[1]).slice(0, 4).map(([cat, amt]) => (
              <div key={cat} className="flex justify-between text-xs">
                <span className="capitalize text-[#5E6E60]">{cat}</span>
                <span className="font-medium text-[#1C2A20]">₹{amt.toLocaleString()}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="bg-[#FFFFFF] rounded-lg border border-[#2F5233]/30 overflow-hidden">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Date</TableHead>
                <TableHead>Category</TableHead>
                <TableHead>Description</TableHead>
                <TableHead>Amount</TableHead>
                <TableHead>Payment</TableHead>
                <TableHead>Added By</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {/* Inline quick-add row */}
              <TableRow className="bg-[#EEF1EA]/60" data-testid="expense-inline-row">
                <TableCell><Input type="date" value={row.date} onChange={(e) => setRow(p => ({ ...p, date: e.target.value }))} className="h-9 w-36" data-testid="expense-inline-date" /></TableCell>
                <TableCell>
                  <Select value={row.category} onValueChange={(v) => setRow(p => ({ ...p, category: v }))}>
                    <SelectTrigger className="h-9 w-36" data-testid="expense-inline-category"><SelectValue /></SelectTrigger>
                    <SelectContent>{CATEGORIES.map(c => <SelectItem key={c} value={c} className="capitalize">{c}</SelectItem>)}</SelectContent>
                  </Select>
                </TableCell>
                <TableCell><Input value={row.description} onChange={(e) => setRow(p => ({ ...p, description: e.target.value }))} onKeyDown={onKey} placeholder="Description…" className="h-9 min-w-[160px]" data-testid="expense-inline-description" /></TableCell>
                <TableCell><Input type="number" value={row.amount} onChange={(e) => setRow(p => ({ ...p, amount: e.target.value }))} onKeyDown={onKey} placeholder="0" className="h-9 w-28" data-testid="expense-inline-amount" /></TableCell>
                <TableCell>
                  <Select value={row.payment_method} onValueChange={(v) => setRow(p => ({ ...p, payment_method: v }))}>
                    <SelectTrigger className="h-9 w-32" data-testid="expense-inline-method"><SelectValue /></SelectTrigger>
                    <SelectContent>{METHODS.map(m => <SelectItem key={m} value={m} className="capitalize">{m.replace('_', ' ')}</SelectItem>)}</SelectContent>
                  </Select>
                </TableCell>
                <TableCell className="text-[#5E6E60] text-xs">You</TableCell>
                <TableCell>
                  <Button size="sm" onClick={addRow} disabled={saving} className="bg-[#2F5233] hover:bg-[#1E3226] h-9" data-testid="expense-inline-add"><Check size={16} className="mr-1" /> Add</Button>
                </TableCell>
              </TableRow>

              {expenses.length === 0 ? (
                <TableRow><TableCell colSpan={7} className="text-center text-[#5E6E60] py-8">No expenses yet. Type in the row above and press Enter to add.</TableCell></TableRow>
              ) : expenses.map(expense => (
                <TableRow key={expense.id}>
                  <TableCell>{fmtDate(expense.date)}</TableCell>
                  <TableCell><span className="capitalize bg-[#EEF1EA] px-2 py-1 rounded text-xs font-medium">{expense.category}</span></TableCell>
                  <TableCell className="font-medium">{expense.description}</TableCell>
                  <TableCell className="text-red-600 font-medium">₹{expense.amount.toLocaleString()}</TableCell>
                  <TableCell className="capitalize">{expense.payment_method}</TableCell>
                  <TableCell>{expense.created_by}</TableCell>
                  <TableCell>
                    <Button size="sm" variant="ghost" onClick={() => deleteExpense(expense.id)} className="text-red-600" data-testid={`delete-expense-${expense.id}`}><Trash2 size={16} /></Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>
    </div>
  );
};
